// @ts-strict
import { modelOptions, mongoose, PropType, post, prop, type Ref, Severity } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, milliseconds, ObjectIdString, validate } from "../../zod-decorator/index.js";
import type { Organization } from "../organization/organization.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { IrrigationSetFieldsWithDefaults, IrrigationSetSchemaTypes } from "../types/irrigationSet.d.js";
import type { DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "IrrigationSet" as const;

@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "IrrigationSets",
        timestamps: true,
        _id: true,
    },
})
@post<SchemaBase<IrrigationSet>>(...addSchemaKeyHook(SCHEMA_KEY))
export class IrrigationSet extends SchemaBase<IrrigationSet, IrrigationSetFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public createdAt?: Date;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public updatedAt?: Date;

    @validate({ zodValidator: z.string().min(1) })
    @prop({ type: () => String, required: true })
    public name!: string;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, ref: "Organization", required: true })
    public owner!: Ref<Organization>;

    @validate({ zodValidator: z.boolean().optional(), responseOnly: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public deleted: boolean = false;

    @validate({ zodValidator: z.record(z.string(), z.number()).optional() })
    @prop({ type: () => Object, allowMixed: Severity.ALLOW, required: false })
    public defaultZoneWateringPercentages?: Record<string, number>;

    @validate({ zodValidator: z.record(z.string(), z.number()).optional() })
    @prop({ type: () => Object, allowMixed: Severity.ALLOW, required: false })
    public chunkByZoneID?: Record<string, number>;

    // Signed offset between chunks (negative overlaps them). Not the shared `milliseconds` helper: that is
    // min(0), and this field is also fractional — Gap Duration is parseFloat(hours|min|days) * conversion.
    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public interChunkGap?: number;

    @validate({ zodValidator: milliseconds.optional() })
    @prop({ type: () => Number, required: false })
    public defaultIrrigationDuration?: number;

    @validate({ zodValidator: z.array(z.string()).optional() })
    @prop({ type: () => String, required: false }, PropType.ARRAY)
    public pausedZones?: string[];

    @validate({ zodValidator: z.array(z.string()).optional() })
    @prop({ type: () => String, required: false }, PropType.ARRAY)
    public zones?: string[];

    @validate({ zodValidator: z.record(z.string(), z.any()).optional() })
    @prop({ type: () => Object, allowMixed: Severity.ALLOW, required: false })
    public meta?: Record<string, never>;

    static getters = {} as const satisfies Record<string, (self: IrrigationSet) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<IrrigationSetSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<IrrigationSet, IrrigationSetFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

IrrigationSet.dtoValidators = getZodSchemaFromClass(
    IrrigationSet,
) as unknown as DtoValidatorTypes<IrrigationSetSchemaTypes>;
